import axios from "axios";
import { authHeaders, isNative } from "@/lib/session";

// Resolve backend URL with a smart fallback for the custom production domain.
// On balagha.tech / www.balagha.tech, the production env may point to the apex
// (e.g. https://balagha.tech) while users land on the www subdomain — that
// triggers an apex→www 308 redirect on /api/*, which browsers block on CORS
// preflight. We force the canonical Emergent backend host in that case so
// every request stays same-CORS-policy and reaches a healthy origin.
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  const PROD = "https://balagha-lessons.emergent.host";
  if (typeof window !== "undefined") {
    const host = window.location.hostname || "";
    if (host === "balagha.tech" || host === "www.balagha.tech") {
      return window.location.origin; // same-origin → first-party cookie (Safari ITP-safe)
    }
    // Capacitor native shell (localhost / capacitor://) has no same-origin API
    if (host === "localhost" || host === "" || window.Capacitor?.isNativePlatform?.()) {
      return envUrl || PROD;
    }
  }
  return envUrl || PROD;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Anonymous device id — used as fallback identity before user signs in.
export function getAnonId() {
  let uid = localStorage.getItem("balagha_uid");
  if (!uid) {
    uid =
      "anon_" +
      Math.random().toString(36).slice(2, 10) +
      Date.now().toString(36).slice(-4);
    localStorage.setItem("balagha_uid", uid);
  }
  return uid;
}

const http = axios.create({
  baseURL: API,
  timeout: 60000,
  withCredentials: true,
});

http.interceptors.request.use((config) => {
  // Always send anon-id; backend uses it only if no auth cookie present.
  config.headers["X-Anon-Id"] = getAnonId();
  Object.assign(config.headers, authHeaders());
  return config;
});

export const api = {
  // public
  listLessons: () => http.get("/lessons").then((r) => r.data),
  getLesson: (id) => http.get(`/lessons/${id}`).then((r) => r.data),

  // progress
  myProgress: () => http.get("/progress/me").then((r) => r.data),
  completeLesson: (id, body) =>
    http.post(`/lessons/${id}/complete`, body).then((r) => r.data),

  // AI
  evaluate: (body) => http.post("/evaluate", body).then((r) => r.data),
  chat: (body) => http.post("/chat", body).then((r) => r.data),
  chatFree: (body) => http.post("/chat-free", body).then((r) => r.data),
  improve: (body) => http.post("/improve", body).then((r) => r.data),

  // challenges
  listChallenges: () => http.get("/challenges").then((r) => r.data),
  submitChallenge: (id, text) =>
    http.post(`/challenges/${id}/submit`, { text }).then((r) => r.data),

  // milestones
  listMilestones: () => http.get("/milestones").then((r) => r.data),

  // auth
  me: () => http.get("/auth/me").then((r) => r.data),
  exchangeSession: (session_id, anon_user_id) =>
    http
      .post("/auth/session", { session_id, anon_user_id, native: isNative })
      .then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  deleteAccount: () => http.delete("/auth/account").then((r) => r.data),
  certificateLink: (trackId) => http.post(`/tracks/${trackId}/certificate/link`).then((r) => r.data),
  appleConfig: () => http.get("/auth/apple/config").then((r) => r.data),
  appleCallback: (identity_token, name) =>
    http
      .post("/auth/apple/callback", {
        identity_token,
        name: name || null,
        anon_user_id: getAnonId(),
        native: isNative,
      })
      .then((r) => r.data),

  // tracks (curriculum map)
  listTracks: () => http.get("/tracks").then((r) => r.data),

  // tests
  getTest: (testId) => http.get(`/tests/${testId}`).then((r) => r.data),
  submitTest: (testId, answers) =>
    http.post(`/tests/${testId}/submit`, { answers }).then((r) => r.data),

  // subscriptions
  mySubscription: () => http.get("/subscription/me").then((r) => r.data),
  startTrial: () =>
    http.post("/subscription/start-trial", {}).then((r) => r.data),
  createCheckout: (plan) =>
    http.post("/checkout/create", { plan }).then((r) => r.data),
  mockConfirm: (payment_id, outcome = "paid") =>
    http
      .post("/checkout/mock-confirm", { payment_id, outcome })
      .then((r) => r.data),
  cancelSubscription: () =>
    http.post("/subscription/cancel", {}).then((r) => r.data),
  // App Store / Play "Restore Purchases" — on native the shell posts entitlements
  // to /api/iap/restore; on web we simply re-check server state.
  restorePurchases: () =>
    http.post("/iap/restore", {}).then((r) => r.data).catch(() => null),

  // public forms
  sendContact: (body) => http.post("/contact", body).then((r) => r.data),
  requestPartnership: (body) =>
    http.post("/partnership/request", body).then((r) => r.data),
};
